import React, { useEffect, useRef } from 'react'
import { isMobile } from 'lib/device'
import cn from 'classnames'
// Direction detection intentionally omitted for now – keep logic simple

export default function WithSelection(props) {
    const wrapperRef = useRef(null)
    // no persistent gating refs; we act only for the local gesture

    // Helper to enable/disable swiper touch moves
    function setSwipe(allow) {
        try {
            const swiperContainer = document.querySelector('.swiper-container')
            const swiper =
                swiperContainer &&
                /** @type {HTMLElement & {swiper?: {allowTouchMove: boolean}}} */ (swiperContainer)
                    .swiper
            if (swiper && typeof swiper.allowTouchMove !== 'undefined') {
                swiper.allowTouchMove = !!allow
            }
        } catch (e) {}
    }

    // no persistent focus-based global gating here — we handle it per-gesture below

    // Simple selection-based touch gating: only disable swipe during touches
    // when a non-collapsed selection exists inside our wrapper. Never stop propagation.
    useEffect(() => {
        if (!isMobile) return
        const el = (props?.noSwipeRef && props.noSwipeRef.current) || wrapperRef.current
        if (!el) return

        // Only apply selection-based gating for title content or NewAnswer context
        const applies = props?.isTitle || props?.swipeWithFocus === false
        if (!applies) return

        const selectionActiveInsideWrapper = () => {
            try {
                const sel = window.getSelection()
                if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false
                const anchor = sel.anchorNode
                const node = anchor && (anchor.nodeType === 1 ? anchor : anchor.parentElement)
                return !!(node && el.contains(node))
            } catch (e) {
                return false
            }
        }

        const isFocusedEditableInside = () => {
            try {
                // Tiptap
                const tiptap = el.querySelector(
                    '.tiptap-editor-container:not(.readonly) .ProseMirror',
                )
                if (
                    tiptap &&
                    tiptap.isContentEditable &&
                    (tiptap === document.activeElement || tiptap.contains(document.activeElement))
                )
                    return true
                // Quill
                const quill = el.querySelector('.ql-editor')
                if (
                    quill &&
                    quill.isContentEditable &&
                    (quill === document.activeElement || quill.contains(document.activeElement))
                )
                    return true
            } catch (e) {}
            return false
        }

        const onTouchStart = ev => {
            if (!el.contains(ev.target)) return
            const shouldDisable =
                selectionActiveInsideWrapper() ||
                (props?.swipeWithFocus === false && isFocusedEditableInside())
            if (shouldDisable) {
                setSwipe(false)
            } else {
                setSwipe(true)
            }
        }

        const onTouchEnd = () => {
            // Always release swipe after this local gesture ends
            setSwipe(true)
        }

        el.addEventListener('touchstart', onTouchStart, { passive: true })
        el.addEventListener('touchend', onTouchEnd, { passive: true })
        el.addEventListener('touchcancel', onTouchEnd, { passive: true })

        return () => {
            try {
                el.removeEventListener('touchstart', onTouchStart)
                el.removeEventListener('touchend', onTouchEnd)
                el.removeEventListener('touchcancel', onTouchEnd)
            } catch (e) {}
        }
    }, [props?.isTitle, props?.swipeWithFocus, props?.noSwipeRef?.current])

    // (Reverted) region-wide swipe disable
    const squashEvent = ev => {
        //console.log('squash ev')
        ev.preventDefault()
        ev.stopPropagation()
    }

    /*
    const squashEventSelected = ev => {
        if (window.getSelection().toString().length > 0) {
            console.log('squash selected')
            //squashEvent(ev)
        }
    }
    */

    if (props.editMode) {
        return <div className="no-op-with-selection-comp">{props.children}</div>
    } else {
        // For mobile, selection only works on title messages. To get drag to work we must set draggable and noselect dynamically in the dnd wrapper.
        return (
            <div
                className={cn('with-selection-comp')}
                ref={wrapperRef}
                style={{
                    fontSize: 0,
                    letterSpacing: 0,
                    wordSpacing: 0,
                    width: '100%',
                }}
            >
                {isMobile ? (
                    <>
                        {props.isTitle ? (
                            <span
                                className={'with-selection-comp title-selection'}
                                onMouseDown={ev => ev.stopPropagation()}
                            >
                                {props.children}
                            </span>
                        ) : (
                            <span className={'with-selection-comp'}>{props.children}</span>
                        )}
                    </>
                ) : (
                    <div
                        className={'with-selection-comp'}
                        draggable
                        onDragStart={squashEvent}
                        // This is to prevent event from bubbling to toggleSelectedAnswer so that it won't clear selection, but will on blur.
                        //onMouseDown={ev => ev.stopPropagation()}
                        //onClick={ev => ev.stopPropagation()}
                        style={{
                            // display: 'inline-block',
                            userSelect: 'text',
                            margin: 0,
                        }} // This is so we limit the selection to only the text contents.
                    >
                        <span>{props.children}</span>
                    </div>
                )}
            </div>
        )
    }
}
